import { gql, useMutation } from '@apollo/client';
import { Refresh } from '@mui/icons-material';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Alert } from '@ovComponents/2-component/alert/alert';
import { Button } from '@ovComponents/2-component/button/button';
import { CircularProgress } from '@ovComponents/2-component/circularProgress/circularProgress';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const SEND_INTEGRATION_FORM_PACK = gql`
  mutation sendIntegrationFormPack($input: SendIntegrationFormPackInput!) {
    sendIntegrationFormPack(input: $input) {
      integrationFormPack {
        id
      }
    }
  }
`;

export const SendIntegrationFormPack = ({
  onNext,
  onBack,
  workflowCompletion,
  direction,
}: {
  onNext: () => void;
  onBack: () => void;
  workflowCompletion: any;
  direction: 'FORWARD' | 'BACKWARD';
}) => {
  const { t } = useTranslation(['workflowCompletions', 'shared']);
  const { workflowData } = useContext(WorkflowContext);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const onBackRef = useRef(onBack);
  const hasAttemptedRef = useRef(false);

  onBackRef.current = onBack;

  const [sendIntegrationFormPack] = useMutation(SEND_INTEGRATION_FORM_PACK, {
    onCompleted: () => {
      setError(null);
      onNext();
    },
  });

  const attemptSendIntegrationFormPack = useCallback(async () => {
    if (
      !workflowCompletion.organization?.id ||
      !workflowData.integrationFormPackId
    ) {
      return;
    }

    setIsLoading(true);
    setError(null);

    const variables = {
      input: {
        integrationFormPackId: workflowData.integrationFormPackId,
      },
      skipErrorHandler: true,
    };

    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        await sendIntegrationFormPack({ variables });
        return;
      } catch (err) {
        if (attempt === 3) {
          setError(err as Error);
        }
      }
    }
    setIsLoading(false);
  }, [workflowCompletion, workflowData, sendIntegrationFormPack]);

  useEffect(() => {
    if (hasAttemptedRef.current) {
      return;
    }
    hasAttemptedRef.current = true;
    if (direction === 'BACKWARD') {
      onBackRef.current();
      return;
    }
    void attemptSendIntegrationFormPack();
  }, [attemptSendIntegrationFormPack, direction]);

  return (
    <Box
      display='flex'
      flexDirection='column'
      alignItems='center'
      justifyContent='center'
      gap={1}
      minHeight='300px'
    >
      <Typography variant='headingMedium'>
        {t('workflowCompletions:sendIntegrationFormPack.title')}
      </Typography>
      <Typography variant='bodyMedium' sx={{ mb: 4 }}>
        {t('workflowCompletions:sendIntegrationFormPack.description')}
      </Typography>
      {error && (
        <Box mb={2}>
          <Alert severity='error'>{error.message}</Alert>
          <Box mt={2} display='flex' justifyContent='center'>
            <Button
              onClick={attemptSendIntegrationFormPack}
              disabled={isLoading}
              label={t('shared:retry')}
              variant='text'
              leadingIcon={Refresh}
            />
          </Box>
        </Box>
      )}
      {isLoading && <CircularProgress />}
    </Box>
  );
};
